import { useCallback, useEffect, useRef, useState } from 'react';
import { useRevalidator } from 'react-router';
import { startAuthentication } from '@simplewebauthn/browser';

import { useTracker } from '@archipro-website/tracker';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { cn } from '@rocco/utils/cn';
import type { AuthSource } from '~/modules/user/type';
import { PASSKEY_LOGIN_OPTIONS_API_ROUTE } from '~/routes/remix-api.user.passkey-login-options';
import { PASSKEY_LOGIN_VERIFY_API_ROUTE } from '~/routes/remix-api.user.passkey-login-verify';
import { derivePlatformBiometricLabel } from '~/modules/user/passkey/platform-label';
import { startMinimumHold } from '~/modules/user/passkey/minimum-hold';
import { getPasskeyIntentCopy, welcomeBackGreeting } from './auth-intent-copy';
import { GateHeading, GateSubcopy } from './GateHeading';
import { GateStickyFooter } from './GateStickyFooter';
import { SecureSignInGlyph } from './SecureSignInGlyph';

export interface PasskeyLoginStepProps {
    /** Host surface, attributed on the funnel events. */
    authSource: AuthSource;
    /** Display name from the device hint for the greeting. */
    firstName?: string;
    /** The assertion verified and the web session is established. */
    onSuccess: () => void;
    /** Step aside to the regular auth step 0 (refusal or user choice). */
    onFallback: () => void;
    /**
     * Refresh loader data on success. In-place hosts (enquiry gate) need it —
     * the raw-fetch verify bypasses Remix's revalidation; redirect hosts skip
     * it because their full-page reload refreshes every loader anyway.
     */
    refreshOnSuccess?: boolean;
    className?: string;
}

const GENERIC_FAILURE =
    'Secure Sign-In failed. Please try another way to log in.';

/** Every ceremony animates for at least this long, however fast the API is. */
const MIN_CEREMONY_MS = 350;

/**
 * Step-0 passkey login (BC-4832): shown instead of the auth modal's initial
 * step when this device holds an enrolled-passkey hint. Mounting immediately
 * runs the discoverable-credential ceremony (the platform sheet appears
 * without an extra tap where the browser allows it); a dismissed prompt drops
 * to an explicit "Log in with Face ID" button, and a server refusal falls
 * back to the regular step silently.
 */
export const PasskeyLoginStep = ({
    authSource,
    firstName,
    onSuccess,
    onFallback,
    refreshOnSuccess,
    className,
}: PasskeyLoginStepProps) => {
    const tracker = useTracker();
    const revalidator = useRevalidator();
    // The glyph sits directly above the pinned actions on the sheet, where
    // the desktop card's flexible spacer collapses to nothing.
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const [busy, setBusy] = useState(true);
    const [succeeded, setSucceeded] = useState(false);
    const [error, setError] = useState<string>();

    const platformLabel = derivePlatformBiometricLabel();

    const runCeremony = useCallback(
        async (auto: boolean) => {
            setBusy(true);
            setError(undefined);
            const settle = startMinimumHold(MIN_CEREMONY_MS);
            try {
                const optionsResponse = await fetch(
                    PASSKEY_LOGIN_OPTIONS_API_ROUTE,
                    { method: 'POST' },
                );
                const options = (await optionsResponse.json()) as
                    | { ok: true; optionsJSON: string }
                    | { ok: false; message: string };
                if (!options.ok) {
                    throw new Error(options.message);
                }

                const assertion = await startAuthentication({
                    optionsJSON: JSON.parse(options.optionsJSON),
                });

                const verifyBody = new FormData();
                verifyBody.append('credential', JSON.stringify(assertion));
                const verifyResponse = await fetch(
                    PASSKEY_LOGIN_VERIFY_API_ROUTE,
                    { method: 'POST', body: verifyBody },
                );
                const verified = (await verifyResponse.json()) as
                    | { ok: true; sessionEstablished: boolean }
                    | { ok: false; message: string };
                if (!verified.ok) {
                    throw new Error(verified.message);
                }
                if (!verified.sessionEstablished) {
                    // ok without a session (missing GraphQL data) — the login
                    // did not actually land; never complete the flow on it.
                    throw new Error(GENERIC_FAILURE);
                }

                // No settle() here: the check-draw animation plays while the
                // host completes the login, so holding it back would only
                // serialise delay into the flow.
                setSucceeded(true);
                if (refreshOnSuccess) {
                    revalidator.revalidate();
                }
                onSuccess();
            } catch (err) {
                // Dismissed platform prompt — or the automatic attempt was
                // refused for lack of user activation. Either way the manual
                // button takes over.
                if (err instanceof Error && err.name === 'NotAllowedError') {
                    await settle();
                    setBusy(false);
                    return;
                }
                console.warn('[SecureSignIn] login failed', err);
                if (auto) {
                    // The automatic attempt failed before the user chose
                    // anything (server refusal, revoked credential) — step
                    // aside to the regular flow rather than surfacing noise.
                    await settle();
                    onFallback();
                    return;
                }
                const isServerMessage =
                    err instanceof Error &&
                    err.name === 'Error' &&
                    err.message !== '';
                await settle();
                setError(isServerMessage ? err.message : GENERIC_FAILURE);
                setBusy(false);
            }
        },
        [onSuccess, onFallback, refreshOnSuccess, revalidator],
    );

    const startedRef = useRef(false);
    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        tracker.log('PasskeyLoginPromptShown', {
            url: new URL(window.location.href),
            data: { Source: authSource },
        });
        void runCeremony(true);
    }, [runCeremony, tracker, authSource]);

    return (
        <div
            className={cn(
                'mx-auto flex min-h-0 w-full max-w-90.75 flex-1 flex-col',
                className,
            )}
        >
            <GateHeading className="mt-3">
                {welcomeBackGreeting(firstName)}
            </GateHeading>
            <GateSubcopy className="leading-normal">
                {/* The gate's intent, then the enrolment ask's speed promise —
                    step 0 has no frame of its own, so the promise is borrowed
                    rather than inventing a second voice for the same feature. */}
                {getPasskeyIntentCopy(authSource)}
            </GateSubcopy>
            <div className={cn('mt-10.5', isMobile && 'mb-10')}>
                <SecureSignInGlyph
                    state={succeeded ? 'success' : busy ? 'busy' : 'idle'}
                />
            </div>
            {error && (
                <p className="mt-4 text-center text-[0.8125rem] text-destructive">
                    {error}
                </p>
            )}
            <div className="flex-1" />
            <GateStickyFooter desktopClassName="mb-9">
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => void runCeremony(false)}
                    className="flex h-14 w-full items-center justify-center rounded-full bg-[#181818] text-[1.125rem] font-medium tracking-[-0.02em] text-[#f3f3f3] shadow-[0_0.1875rem_0.8125rem_0_rgba(0,0,0,0.25),0_1.25rem_2.125rem_0_rgba(0,0,0,0.25)] transition-colors hover:bg-[#2c2c2c] disabled:cursor-not-allowed"
                >
                    {succeeded
                        ? 'Logged in.'
                        : busy
                          ? 'Authenticating.'
                          : `Log in with ${platformLabel ?? 'Secure Sign-In'}`}
                </button>
                <button
                    type="button"
                    disabled={busy}
                    onClick={onFallback}
                    className="mt-6 w-full text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]"
                >
                    Use another way to log in
                </button>
            </GateStickyFooter>
        </div>
    );
};
